const STORAGE_KEY = 'twin-tales-youtube-publish-draft';

/** @typedef {import('react').Dispatch<import('react').SetStateAction<string>>} SetStr */
/** @typedef {import('react').Dispatch<import('react').SetStateAction<boolean>>} SetBool */

/**
 * Map a story / plan language code to YouTube publish fields.
 * Optimize/title select currently supports English + Hindi; other codes still
 * fill default_language / default_audio_language for the upload API.
 *
 * @returns {{ optimizeLang: 'en' | 'hi', defaultLanguage: string, defaultAudioLanguage: string } | null}
 */
export function resolveYoutubePublishLanguage(storyLanguage) {
  const raw = String(storyLanguage || '').trim().toLowerCase();
  if (!raw || raw === 'auto') return null;
  const base = raw.split(/[-_]/)[0] || raw;
  if (base === 'hi' || raw === 'hindi' || /[\u0900-\u097f]/.test(String(storyLanguage || ''))) {
    return { optimizeLang: 'hi', defaultLanguage: 'hi', defaultAudioLanguage: 'hi' };
  }
  if (base === 'en' || raw === 'english') {
    return { optimizeLang: 'en', defaultLanguage: 'en', defaultAudioLanguage: 'en' };
  }
  const code = raw.slice(0, 12);
  return {
    optimizeLang: 'en',
    defaultLanguage: code,
    defaultAudioLanguage: code,
  };
}

/**
 * Serializable YouTube upload form snapshot (survives OAuth popup / new tab).
 */
export function buildYoutubePublishDraft(fields) {
  return {
    videoUrl: fields.videoUrl ?? '',
    title: fields.title ?? '',
    description: fields.description ?? '',
    tagsRaw: fields.tagsRaw ?? '',
    categoryId: fields.categoryId ?? '22',
    privacyStatus: fields.privacyStatus ?? 'private',
    thumbnailUrl: fields.thumbnailUrl ?? '',
    defaultLanguage: fields.defaultLanguage ?? '',
    defaultAudioLanguage: fields.defaultAudioLanguage ?? '',
    metadataOptimizeLang: fields.metadataOptimizeLang ?? 'en',
    youtubeShortsMode: Boolean(fields.youtubeShortsMode),
    playlistId: fields.playlistId ?? '',
    notifySubscribers: Boolean(fields.notifySubscribers),
    embeddable: fields.embeddable !== false,
    license: fields.license ?? 'youtube',
    madeForKids: Boolean(fields.madeForKids),
    thumbFrameUrls: Array.isArray(fields.thumbFrameUrls) ? fields.thumbFrameUrls : [],
  };
}

/**
 * @param {ReturnType<typeof buildYoutubePublishDraft>} draft
 * @param {Record<string, SetStr | SetBool | import('react').Dispatch<import('react').SetStateAction<string[]>>>} setters
 */
export function applyYoutubePublishDraft(draft, setters) {
  if (!draft || typeof draft !== 'object') return;
  const apply = (key, value) => {
    const fn = setters[key];
    if (typeof fn === 'function') fn(value);
  };
  apply('setVideoUrl', draft.videoUrl ?? '');
  apply('setTitle', draft.title ?? '');
  apply('setDescription', draft.description ?? '');
  apply('setTagsRaw', draft.tagsRaw ?? '');
  apply('setCategoryId', draft.categoryId ?? '22');
  apply('setPrivacyStatus', draft.privacyStatus ?? 'private');
  apply('setThumbnailUrl', draft.thumbnailUrl ?? '');
  apply('setDefaultLanguage', draft.defaultLanguage ?? '');
  apply('setDefaultAudioLanguage', draft.defaultAudioLanguage ?? '');
  apply('setMetadataOptimizeLang', draft.metadataOptimizeLang ?? 'en');
  apply('setYoutubeShortsMode', Boolean(draft.youtubeShortsMode));
  apply('setPlaylistId', draft.playlistId ?? '');
  apply('setNotifySubscribers', Boolean(draft.notifySubscribers));
  apply('setEmbeddable', draft.embeddable !== false);
  apply('setLicense', draft.license ?? 'youtube');
  apply('setMadeForKids', Boolean(draft.madeForKids));
  if (Array.isArray(draft.thumbFrameUrls) && draft.thumbFrameUrls.length) {
    apply('setThumbFrameUrls', draft.thumbFrameUrls);
  }
}

export function saveYoutubePublishDraft(draft) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
  } catch {
    /* quota / private mode */
  }
}

export function loadYoutubePublishDraft() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearYoutubePublishDraft() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Mirror server upload boost so the form shows what YouTube will receive. */
export function applyShortsDescriptionBoost(description) {
  const desc = String(description || '').trim();
  const compact = desc.toLowerCase().replace(/\s/g, '').replace(/\u200c/g, '');
  if (!compact) {
    return '#Shorts\n#YouTubeShorts';
  }
  if (!compact.includes('#shorts')) {
    return `${desc}\n\n#Shorts\n#YouTubeShorts`.slice(0, 5000);
  }
  if (!compact.replace(/_/g, '').includes('youtubeshorts')) {
    return `${desc}\n#YouTubeShorts`.slice(0, 5000);
  }
  return desc.slice(0, 5000);
}
